import { useEffect, useState } from "react";
import { SessionRecord } from "./RecordEditor";
import type { ActiveSession, Exercise } from "../shared/types";
import { formatExerciseResult } from "../shared/history";
import { api } from "./api";
import {
  Card,
  Empty,
  ErrorNotice,
  formatDate,
  formatDuration,
  Modal,
  Spinner,
} from "./components";

interface SessionRow {
  id: string;
  code: string;
  name: string;
  startedAt: string;
  completedAt: string | null;
  elapsedSeconds: number | null;
  status: string;
  setCount: number;
}
interface DetailSet {
  id: string;
  name: string;
  loadMode: string;
  setNumber: number;
  weightValue: number | null;
  reps: number | null;
  countValue: number | null;
  exerciseNote?: string | null;
  substitutedFromName?: string | null;
}
interface SessionDetail {
  template: { code: string; name: string };
  startedAt: string;
  completedAt: string | null;
  elapsedSeconds: number | null;
  status: string;
  notes?: string | null;
  exerciseEvents?: Array<{
    kind: "skipped" | "substituted" | "note";
    exerciseName: string;
    detail?: string | null;
  }>;
  sets: DetailSet[];
}
interface ExerciseHistoryRow {
  sessionId: string;
  completedAt: string;
  weightValue: number | null;
  reps: number | null;
  countValue: number | null;
  setNumber: number;
}
interface ExerciseHistoryResponse {
  loadMode: Exercise["loadMode"];
  personalBest: { metric: "weight" | "count" | "reps"; value: number | null };
  trend: ExerciseHistoryRow[];
}

function groupSets(sets: DetailSet[]): Array<[string, DetailSet[]]> {
  const groups = new Map<string, DetailSet[]>();
  for (const set of sets)
    groups.set(set.name, [...(groups.get(set.name) ?? []), set]);
  return [...groups.entries()];
}

export function History({profileId,onWorkout}: {profileId:string; onWorkout:(session:ActiveSession)=>void}) {
  const [recordId,setRecordId] = useState<string|null>(null);
  const [rows, setRows] = useState<SessionRow[] | null>(null);
  const [detail, setDetail] = useState<SessionDetail | null>(null);
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [exerciseHistory, setExerciseHistory] = useState<{
    exercise: Exercise;
    data: ExerciseHistoryResponse | null;
  } | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api<SessionRow[]>("/history")
      .then(setRows)
      .catch((e) => setError(e.message));
    api<Exercise[]>("/exercises")
      .then(setExercises)
      .catch(() => {
        /* session history remains usable */
      });
  }, []);
  async function open(id: string) { setRecordId(id); }
  async function openExercise(exerciseId: string) {
    const exercise = exercises.find((item) => item.id === exerciseId);
    if (!exercise) return;
    setExerciseHistory({ exercise, data: null });
    try {
      setExerciseHistory({
        exercise,
        data: await api<ExerciseHistoryResponse>(
          `/exercises/${exercise.id}/history`,
        ),
      });
    } catch (e) {
      setExerciseHistory(null);
      setError(
        e instanceof Error ? e.message : "Could not load exercise history",
      );
    }
  }
  if (!rows && !error) return <Spinner label="Loading workout history" />;
  return (
    <>
      <header className="page-heading">
        <p className="eyebrow">Permanent record</p>
        <h1>History</h1>
        <p>
          Every completed, partial and skipped session stays attached to your
          profile.
        </p>
      </header>
      {error && <ErrorNotice message={error} />}{" "}
      {rows?.length === 0 && (
        <Empty
          title="No completed workouts yet"
          body="Your first completed workout will appear here with every set, weight and rep."
        />
      )}
      <Card>
        <h2>Exercise history, PB &amp; trend</h2>
        <p className="muted">Open any exercise without starting a workout.</p>
        <label className="field">
          <span className="field__label">Exercise</span>
          <select
            defaultValue=""
            onChange={(event) => {
              if (event.target.value) void openExercise(event.target.value);
            }}
          >
            <option value="">Choose an exercise…</option>
            {exercises.map((exercise) => (
              <option key={exercise.id} value={exercise.id}>
                {exercise.name}
              </option>
            ))}
          </select>
        </label>
      </Card>
      <div className="session-list">
        {rows?.map((row) => (
          <button
            className="session-row"
            onClick={() => open(row.id)}
            key={row.id}
          >
            <span
              className={`workout-letter ${row.status === "completed" ? "workout-letter--done" : "workout-letter--attention"}`}
            >
              {row.code}
            </span>
            <span>
              <strong>{row.name || `Workout ${row.code}`}</strong>
              <small>
                {formatDate(row.completedAt ?? row.startedAt)} ·{" "}
                {formatDuration(row.elapsedSeconds)}
              </small>
            </span>
            <span>
              <strong>{row.setCount}</strong>
              <small>sets</small>
            </span>
            <span aria-hidden="true">›</span>
          </button>
        ))}
      </div>
      <a className="button button--secondary" href="/api/export.csv" download>
        Export my workout CSV
      </a>
      {recordId && <SessionRecord id={recordId} profileId={profileId} onClose={()=>setRecordId(null)} onResume={onWorkout} onChanged={()=>{void api<SessionRow[]>("/history").then(setRows);}}/>}
      {detail && (
        <Modal
          title={`Workout ${detail.template?.code ?? ""} · ${formatDate(detail.completedAt ?? detail.startedAt)}`}
          onClose={() => setDetail(null)}
        >
          <p>
            {formatDuration(detail.elapsedSeconds)} ·{" "}
            <span className="pill">{detail.status}</span>
          </p>
          {detail.notes && (
            <section className="history-context">
              <h3>Session notes</h3>
              <p>{detail.notes}</p>
            </section>
          )}
          {detail.exerciseEvents?.length ? (
            <section className="history-context">
              <h3>Workout changes &amp; exercise notes</h3>
              <ul>
                {detail.exerciseEvents.map((event, index) => (
                  <li key={`${event.kind}-${event.exerciseName}-${index}`}>
                    <strong>{event.exerciseName}</strong>: {event.kind}
                    {event.detail ? ` · ${event.detail}` : ""}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
          <div className="history-detail">
            {groupSets(detail.sets).map(([name, sets]) => (
              <section key={name}>
                <h3>{name}</h3>
                <ol>
                  {sets.map((set) => (
                    <li key={set.id}>
                      Set {set.setNumber}:{" "}
                      {set.weightValue !== null
                        ? `${set.weightValue} kg · `
                        : ""}
                      {set.reps !== null
                        ? `${set.reps} reps`
                        : `${set.countValue} count`}
                      {set.substitutedFromName
                        ? ` · substituted for ${set.substitutedFromName}`
                        : ""}
                      {set.exerciseNote ? ` · note: ${set.exerciseNote}` : ""}
                    </li>
                  ))}
                </ol>
              </section>
            ))}
          </div>
        </Modal>
      )}
      {exerciseHistory && !recordId && (
        <Modal
          title={`${exerciseHistory.exercise.name} history`}
          onClose={() => setExerciseHistory(null)}
        >
          {exerciseHistory.data === null ? (
            <Spinner label="Loading exercise history" />
          ) : exerciseHistory.data.trend.length === 0 ? (
            <p>No logged sets yet.</p>
          ) : (
            <>
              <p className="personal-best">
                <strong>
                  Personal best {exerciseHistory.data.personalBest.metric}
                </strong>
                <span>
                  {exerciseHistory.data.personalBest.value === null
                    ? "—"
                    : exerciseHistory.data.personalBest.metric === "count"
                      ? `${exerciseHistory.data.personalBest.value} count`
                      : exerciseHistory.data.personalBest.metric === "reps"
                        ? `Bodyweight · ${exerciseHistory.data.personalBest.value} reps`
                        : exerciseHistory.data.loadMode === "bodyweight"
                          ? `Bodyweight + ${exerciseHistory.data.personalBest.value} kg`
                          : `${exerciseHistory.data.personalBest.value} kg`}
                </span>
              </p>
              <ol className="exercise-history-list">
                {exerciseHistory.data.trend.map((row, index) => (
                  <li key={`${row.sessionId}-${row.setNumber}-${index}`}>
                    <strong>
                      {formatDate(row.completedAt)} · set {row.setNumber}
                    </strong>
                    <span>
                      {formatExerciseResult(
                        exerciseHistory.data!.loadMode,
                        row,
                      )}
                    </span>
                    <button onClick={()=>setRecordId(row.sessionId)}>View / edit workout</button>
                  </li>
                ))}
              </ol>
            </>
          )}
        </Modal>
      )}
    </>
  );
}
